import { useState } from "react";
import styled from "styled-components";
import { useAppSelector } from "../../../app/hooks";
import { deliveryState } from "../../../app/reducers/delivery-slice";
import theme from "../../../style/theme";
import { DeliveryGroupType } from "../../../types/schedule/delivery-group-type";
import useDidMountEffect from "../../../utils/custom-hook/use-did-mount-effect";
import {
  convertToTimeLimited,
  getHour,
  getMidday,
  getMinute,
} from "../../../utils/date/date-format";
import { DeliveryPayloadValidator } from "../../../utils/validator/delivery";
import { SearchHour } from "../../autocomplete/SearchHour";
import { SearchMinute } from "../../autocomplete/SearchMinute";
import { CustomRadioButton } from "../../button/CustomRadioButton";
import { SwitchButton } from "../../button/SwitchButton";
import { GeneralTextField } from "../../text-field/GeneralTextField";
import { ActionForm, WorkPlaceForm } from "./DeliveryDialog";
import { NewLocationSearchCard } from "./NewLocationSearchCard";
import { RecentLocationSearchCard } from "./RecentLocationSearchCard";
import Asterisk from "../../asterisk/asterisk";

interface Props {
  selectedClientId: string;
  actionForm: ActionForm;
  addressForm: string;
  workPlaceForm: WorkPlaceForm;
  deliveryPayloadValidator: DeliveryPayloadValidator;
  dateToWork: Date;
  setActionForm: React.Dispatch<React.SetStateAction<ActionForm>>;
  setAddressForm: React.Dispatch<React.SetStateAction<string>>;
  setWorkPlaceForm: React.Dispatch<React.SetStateAction<WorkPlaceForm>>;
}

const radioMap = {
  "기존 장소": "기존 장소",
  신규입력: "신규입력",
};

export const PickUpActionFormCard: React.FC<Props> = ({
  selectedClientId,
  actionForm,
  addressForm,
  workPlaceForm,
  deliveryPayloadValidator,
  dateToWork,
  setActionForm,
  setAddressForm,
  setWorkPlaceForm,
}) => {
  const { selectedDelivery } = useAppSelector(deliveryState);
  const [selectedKey, setSelectedKey] = useState("기존 장소");
  const [timeLimitedform, setTimeLimitedForm] = useState({
    type: selectedDelivery
      ? getMidday(selectedDelivery.pickUpAction.startTimeLimited)
      : "오전",
    hour: selectedDelivery
      ? getHour(selectedDelivery.pickUpAction.startTimeLimited)
      : "",
    minute: selectedDelivery
      ? getMinute(selectedDelivery.pickUpAction.startTimeLimited)
      : "",
  });

  const handleClickTimeLimitedTypeSwitchButton = (value: string) => {
    setTimeLimitedForm({
      ...timeLimitedform,
      type: value,
    });
  };

  const setTimeLimitedHourForm = (newHour: string) => {
    setTimeLimitedForm({
      ...timeLimitedform,
      hour: newHour,
    });
  };

  const setTimeLimitedMinuteForm = (newMinute: string) => {
    setTimeLimitedForm({
      ...timeLimitedform,
      minute: newMinute,
    });
  };

  const handleChangeActionFormTextField = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setActionForm({
      ...actionForm,
      [event.target.name]: event.target.value,
    });
  };

  useDidMountEffect(() => {
    if (timeLimitedform.hour === "" || isNaN(Number(timeLimitedform.minute))) {
      return;
    }

    setActionForm({
      ...actionForm,
      startTimeLimited: convertToTimeLimited(
        timeLimitedform,
        dateToWork
      ).toISOString(),
      endTimeLimited: convertToTimeLimited(
        timeLimitedform,
        dateToWork
      ).toISOString(),
    });
  }, [timeLimitedform, dateToWork]);

  return (
    <LocationCardContainer>
      <LocationCardTitle>상차지 정보</LocationCardTitle>
      <FlexContainer>
        <FieldContainer>요청 시간<Asterisk/></FieldContainer>
        <SwitchButtonContainer>
          <SwitchButton
            selectTitle="오전"
            active={timeLimitedform.type === "오전"}
            value={"오전"}
            leftBorderRadius
            onClick={handleClickTimeLimitedTypeSwitchButton}
            width="3.625rem"
          />
          <SwitchButton
            selectTitle="오후"
            active={timeLimitedform.type === "오후"}
            value={"오후"}
            rightBorderRadius
            onClick={handleClickTimeLimitedTypeSwitchButton}
            width="3.625rem"
          />
        </SwitchButtonContainer>
        <TimeSelectorContainer>
          <SearchHour
            hour={timeLimitedform.hour}
            setHour={setTimeLimitedHourForm}
          />
        </TimeSelectorContainer>
        <TimeLable>시</TimeLable>
        <TimeSelectorContainer>
          <SearchMinute
            minute={timeLimitedform.minute}
            setMinute={setTimeLimitedMinuteForm}
          />
        </TimeSelectorContainer>
        <TimeLable>분</TimeLable>
      </FlexContainer>
      <FlexContainer>
        <FieldContainer>상차 장소<Asterisk/></FieldContainer>
        <RadioButtonContainer>
          <CustomRadioButton
            radioMap={radioMap}
            selectedKey={selectedKey}
            setSelectedKey={setSelectedKey}
            name={"pick_up"}
          />
        </RadioButtonContainer>
      </FlexContainer>
      <NoMarginFlexContainer>
      {selectedKey === "신규입력" ? (
        <NewLocationSearchCardContainer>
          <NewLocationSearchCard
            addressForm={addressForm}
            setAddressForm={setAddressForm}
            workPlaceForm={workPlaceForm}
            setWorkPlaceForm={setWorkPlaceForm}
            deliveryPayloadValidatorAddress={
              deliveryPayloadValidator.pickUpAddress
            }
            deliveryPayloadValidatorDetail={
              deliveryPayloadValidator.pickUpWorkPlaceDetail
            }
          />
        </NewLocationSearchCardContainer>
      ) : (
        <RecentLocationSearchCardContainer>
          <RecentLocationSearchCard
            clientId={selectedClientId}
            addressForm={addressForm}
            setAddressForm={setAddressForm}
            workPlaceForm={workPlaceForm}
            setWorkPlaceForm={setWorkPlaceForm}
            deliveryPayloadValidatorAddress={
              deliveryPayloadValidator.pickUpAddress
            }
            deliveryPayloadValidatorDetail={
              deliveryPayloadValidator.pickUpWorkPlaceDetail
            }
            type={DeliveryGroupType.PickUp}
          />
        </RecentLocationSearchCardContainer>
      )}
        </NoMarginFlexContainer>
      <NoMarginFlexContainer>
        <FieldContainer>요청사항</FieldContainer>
        <GeneralTextField
          placeholder="배송원이 확인할 고객 요청사항을 입력하세요"
          name="requirement"
          value={actionForm.requirement}
          onChange={handleChangeActionFormTextField}
          width="410px"
        />
      </NoMarginFlexContainer>
    </LocationCardContainer>
  );
};

const LocationCardContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const LocationCardTitle = styled.div`
  font-weight: 600;
  font-size: 1.125rem;
  line-height: 1.375rem;
  color: ${theme.palette.primitives.black};
  margin-bottom: 1.25rem;
`;

const FlexContainer = styled.div`
  display: flex;
  align-items: center;
  margin-top: 1rem;
`;

const NoMarginFlexContainer = styled.div`
  display: flex;
  align-items: center;
`;

const FieldContainer = styled.div`
  width: 88px;
  font-style: normal;
  font-weight: 500;
  font-size: 0.813rem;
  line-height: 1rem;
  color: ${theme.palette.primitives.gray600};
`;

const RadioButtonContainer = styled.div``;

const SwitchButtonContainer = styled.div`
  display: flex;
  width: 7.25rem;
  margin: 0 0.75rem 0 0;
`;

const TimeSelectorContainer = styled.div`
  margin-left: 0.75rem;
`;

const TimeLable = styled.div`
  margin-left: 0.75rem;
  font-weight: 500;
  font-size: 0.938rem;
  line-height: 1.125rem;
  color: ${theme.palette.primitives.gray600};
`;

const RecentLocationSearchCardContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
`;

const NewLocationSearchCardContainer = styled.div`
  display: flex;
  margin-top: 16px;
`;
